import { TrendingDown, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "./cn";

export type TendenciaDoIndicador = "alta" | "baixa" | "neutra";

export type PropsDoCartaoIndicador = {
  readonly rotulo: string;
  /** Valor JÁ formatado. Formatação é responsabilidade do módulo, não do componente. */
  readonly valor: string;
  readonly detalhe?: string | undefined;
  /** Conteudo entre o valor e o detalhe. O total fica em cima; isto, embaixo. */
  readonly nota?: ReactNode;
  readonly tendencia?: TendenciaDoIndicador | undefined;
  /** Ícone do lucide, mostrado no canto. Ajuda a varrer uma linha de KPIs. */
  readonly icone?: ReactNode;
  readonly children?: ReactNode;
  /** Cartao em verde da marca, para o numero principal de um grupo. */
  readonly destaque?: boolean | undefined;
  readonly className?: string | undefined;
  /** Dica ao lado do rotulo (ex.: `<Dica>` explicando o calculo). */
  readonly ajuda?: ReactNode;
};

const COR_DA_TENDENCIA: Readonly<Record<TendenciaDoIndicador, string>> = {
  alta: "bg-success/10 text-success",
  baixa: "bg-destructive/10 text-destructive",
  neutra: "bg-muted text-muted-foreground",
};

/** No cartao de destaque (fundo verde), a tendencia usa tons claros legiveis. */
const COR_DA_TENDENCIA_NO_DESTAQUE: Readonly<Record<TendenciaDoIndicador, string>> = {
  alta: "bg-ice/15 text-ice",
  baixa: "bg-alert/15 text-alert",
  neutra: "bg-white/10 text-white/75",
};

/**
 * Cartão de indicador (KPI). O tijolo de qualquer dashboard.
 *
 * Recebe `valor` como string já formatada de propósito: moeda, porcentagem e locale
 * são decisão do domínio. Se o componente formatasse, cada tela precisaria de uma
 * prop nova (`moeda`, `casasDecimais`, `sufixo`…) e ele viraria um formatador
 * disfarçado de componente visual.
 *
 * A seta da tendência é decorativa (`aria-hidden`); o significado está no texto do
 * detalhe. Indicador que comunica só por cor e seta é ilegível para quem não
 * distingue as duas.
 */
export function CartaoIndicador({
  rotulo,
  valor,
  detalhe,
  nota,
  tendencia,
  icone,
  children,
  destaque = false,
  className,
  ajuda,
}: PropsDoCartaoIndicador) {
  const Seta = tendencia === "baixa" ? TrendingDown : TrendingUp;
  const cores = destaque ? COR_DA_TENDENCIA_NO_DESTAQUE : COR_DA_TENDENCIA;

  return (
    <div
      className={cn(
        "flex flex-col rounded-3xl p-6 transition-shadow",
        destaque
          ? "bg-secondary text-secondary-foreground shadow-xl"
          : "bg-card text-card-foreground shadow-sm ring-1 ring-border/70 hover:shadow-md",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            "flex items-center gap-1.5 text-sm font-medium",
            destaque ? "text-secondary-foreground/75" : "text-muted-foreground",
          )}
        >
          {rotulo}
          {ajuda}
        </p>
        {icone ? (
          <span
            aria-hidden="true"
            className={cn(
              "grid size-9 shrink-0 place-items-center rounded-full [&_svg]:size-4",
              destaque ? "bg-white/10 text-ice" : "bg-accent text-accent-foreground",
            )}
          >
            {icone}
          </span>
        ) : null}
      </div>

      <p className="mt-5 text-[1.75rem] leading-none font-medium tracking-[-0.03em] tabular-nums">
        {valor}
      </p>

      {nota}

      {detalhe !== undefined || tendencia !== undefined ? (
        <p
          className={cn(
            "mt-4 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
            cores[tendencia ?? "neutra"],
          )}
        >
          {tendencia && tendencia !== "neutra" ? (
            <Seta aria-hidden="true" className="size-3.5 shrink-0" />
          ) : null}
          {detalhe}
        </p>
      ) : null}

      {children}
    </div>
  );
}
